import { useTranslation as useI18nTranslation } from 'react-i18next';
import { useCallback } from 'react';

/**
 * Hook personalizado para traducciones
 * Extiende react-i18next con funcionalidades específicas del proyecto
 */
export const useTranslation = (namespace = 'translation') => {
  const { t, i18n } = useI18nTranslation(namespace);

  // Función para cambiar idioma
  const changeLanguage = useCallback((lng) => {
    i18n.changeLanguage(lng);
  }, [i18n]);

  // Función para obtener el idioma actual
  const getCurrentLanguage = useCallback(() => {
    return i18n.language;
  }, [i18n]);

  // Función para verificar si un idioma está disponible
  const isLanguageAvailable = useCallback((lng) => {
    return Object.keys(i18n.services.resourceStore.data).includes(lng);
  }, [i18n]);

  // Función para obtener los idiomas disponibles
  const getAvailableLanguages = useCallback(() => {
    return Object.keys(i18n.services.resourceStore.data);
  }, [i18n]);

  // Traducciones específicas por sección
  const tCommon = useCallback((key, options) => t(`common.${key}`, options), [t]);
  const tAuth = useCallback((key, options) => t(`auth.${key}`, options), [t]);
  const tNav = useCallback((key, options) => t(`navigation.${key}`, options), [t]);
  const tAdmin = useCallback((key, options) => t(`admin.${key}`, options), [t]);
  const tForms = useCallback((key, options) => t(`forms.${key}`, options), [t]);
  const tMessages = useCallback((key, options) => t(`messages.${key}`, options), [t]);
  const tSidebar = useCallback((key, options) => t(`sidebar.${key}`, options), [t]);
  const tContact = useCallback((key, options) => t(`contact.${key}`, options), [t]);
  const tPages = useCallback((key, options) => t(`pages.${key}`, options), [t]);
  const tFooter = useCallback((key, options) => t(`footer.${key}`, options), [t]);
  const tCookieConsent = useCallback((key, options) => t(`cookieConsent.${key}`, options), [t]);
  const tTestimonials = useCallback((key, options) => t(`testimonials.${key}`, options), [t]);
  const tBentoBox = useCallback((key, options) => t(`bentoBox.${key}`, options), [t]);
  const tErrorBoundary = useCallback((key, options) => t(`errorBoundary.${key}`, options), [t]);

  // Función para formatear fechas según el idioma
  const formatDate = useCallback((date, options = {}) => {
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    }).format(new Date(date));
  }, [i18n.language]);

  // Función para formatear números según el idioma
  const formatNumber = useCallback((number, options = {}) => {
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  }, [i18n.language]);

  // Función para formatear moneda
  const formatCurrency = useCallback((amount, currency = 'EUR', options = {}) => {
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...options
    }).format(amount);
  }, [i18n.language]);

  return {
    // Funciones básicas de i18next
    t,
    i18n,
    
    // Funciones de gestión de idioma
    changeLanguage,
    getCurrentLanguage,
    isLanguageAvailable,
    getAvailableLanguages,
    
    // Traducciones por sección
    tCommon,
    tAuth,
    tNav,
    tAdmin,
    tForms,
    tMessages,
    tSidebar,
    tContact,
    tPages,
    tFooter,
    tCookieConsent,
    tTestimonials,
    tBentoBox,
    tErrorBoundary,
    
    // Funciones de formateo
    formatDate,
    formatNumber,
    formatCurrency,
    
    // Estado del idioma
    language: i18n.language,
    isReady: i18n.isInitialized
  };
};

export default useTranslation; 